import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { storeTerminalsAPI, storesAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Monitor, Plus, Settings, Server, Wifi, WifiOff,
  CheckCircle, AlertCircle, Trash2, Edit, RefreshCw,
  ExternalLink, Copy, Info, Users, UserPlus, Printer,
  CreditCard, Tv, DollarSign, Cpu, FileText, Save
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const StoreSettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const storeId = user?.entity_id;

  const [activeTab, setActiveTab] = useState('terminals');
  const [loading, setLoading] = useState(true);
  const [storeInfo, setStoreInfo] = useState(null);

  // Terminals state
  const [terminals, setTerminals] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [newTerminalName, setNewTerminalName] = useState('');
  const [newTerminalType, setNewTerminalType] = useState('pos');
  const [processing, setProcessing] = useState(false);

  // Hardware settings modal state
  const [showHardwareModal, setShowHardwareModal] = useState(false);
  const [hardwareTerminal, setHardwareTerminal] = useState(null);
  const [savingHardware, setSavingHardware] = useState(false);
  
  // All hardware settings in one object
  const [hardwareSettings, setHardwareSettings] = useState({
    // Printer Settings
    printer: {
      enabled: true,
      print_method: 'browser', // 'browser' or 'qz_tray'
      printer_name: '',
      paper_width: 80,
      auto_print: true,
      print_copies: 1
    },
    // Cash Drawer Settings
    cash_drawer: {
      enabled: false,
      connection_type: 'printer', // 'printer' (RJ11) or 'usb'
      auto_open_on: 'cash', // 'cash', 'all', 'manual'
      kick_command: 'ESC p 0 25 250' // Default ESC/POS command
    },
    // Pole Display / Customer Display Settings
    customer_display: {
      enabled: false,
      display_type: 'none', // 'none', 'vfd_pole', 'lcd_pole', 'secondary_screen'
      connection: 'com', // 'com', 'usb', 'network'
      port_or_ip: '',
      show_items: true,
      show_total: true,
      welcome_message: 'Welcome to SY Store!'
    },
    // Payment Terminal Settings
    payment_terminal: {
      enabled: false,
      terminal_brand: 'manual', // 'manual', 'pine_labs', 'paytm', 'razorpay', 'phonepe', 'mswipe'
      connection_type: 'usb', // 'usb', 'bluetooth', 'network'
      device_id: '',
      ip_address: '',
      integration_mode: 'manual' // 'manual' or 'integrated'
    }
  });

  // Legacy printer modal state (keeping for backward compatibility)
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [printerTerminal, setPrinterTerminal] = useState(null);
  const [printerSettings, setPrinterSettings] = useState({
    default_printer: '',
    paper_width: 80,
    auto_print: true,
    print_copies: 1
  });
  const [savingPrinter, setSavingPrinter] = useState(false);

  // Local server state
  const [localServerUrl, setLocalServerUrl] = useState('');
  const [localServerStatus, setLocalServerStatus] = useState(null);
  const [checkingLocalServer, setCheckingLocalServer] = useState(false);

  // Staff assignment state
  const [staffList, setStaffList] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffAssignments, setStaffAssignments] = useState({});

  // Bill Terms state
  const [billTerms, setBillTerms] = useState('');
  const [savingBillTerms, setSavingBillTerms] = useState(false);

  useEffect(() => {
    if (storeId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [terminalsRes, storeRes, staffRes] = await Promise.all([
        storeTerminalsAPI.list(storeId),
        storesAPI.get(storeId),
        storeTerminalsAPI.getStoreStaff(storeId).catch(() => ({ data: { staff: [] } }))
      ]);
      setTerminals(terminalsRes.data.terminals || []);
      setStoreInfo(storeRes.data);
      setStaffList(staffRes.data.staff || []);
      
      // Load bill terms from store
      setBillTerms(storeRes.data?.bill_terms || '');
      
      // Load saved local server URL
      const savedUrl = localStorage.getItem(`local_server_url_${storeId}`);
      if (savedUrl) {
        setLocalServerUrl(savedUrl);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Terminal Management
  const handleAddTerminal = async () => {
    if (!newTerminalName.trim()) {
      toast.error('Please enter terminal name');
      return;
    }

    try {
      setProcessing(true);
      await storeTerminalsAPI.create({
        store_id: storeId,
        terminal_name: newTerminalName,
        terminal_type: newTerminalType
      });

      toast.success('Terminal created successfully');
      setShowAddModal(false);
      setNewTerminalName('');
      setNewTerminalType('pos');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create terminal');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditTerminal = async () => {
    if (!editingTerminal) return;

    try {
      setProcessing(true);
      await storeTerminalsAPI.update(editingTerminal.terminal_id, {
        terminal_name: newTerminalName,
        terminal_type: newTerminalType
      });

      toast.success('Terminal updated');
      setShowEditModal(false);
      setEditingTerminal(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update terminal');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteTerminal = async (terminalId) => {
    if (!window.confirm('Are you sure you want to deactivate this terminal?')) {
      return;
    }

    try {
      await storeTerminalsAPI.delete(terminalId);
      toast.success('Terminal deactivated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete terminal');
    }
  };

  const openEditModal = (terminal) => {
    setEditingTerminal(terminal);
    setNewTerminalName(terminal.terminal_name);
    setNewTerminalType(terminal.terminal_type);
    setShowEditModal(true);
  };

  // Local Server Configuration
  const handleSaveLocalServerUrl = () => {
    if (localServerUrl.trim()) {
      localStorage.setItem(`local_server_url_${storeId}`, localServerUrl.trim());
      toast.success('Local server URL saved');
    }
  };

  const handleCheckLocalServer = async () => {
    if (!localServerUrl.trim()) {
      toast.error('Please enter local server URL first');
      return;
    }

    setCheckingLocalServer(true);
    try {
      const response = await fetch(`${localServerUrl.trim()}/api/store/info`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000)
      });
      
      if (response.ok) {
        const data = await response.json();
        setLocalServerStatus({
          connected: true,
          storeCode: data.store_code,
          storeName: data.store_name,
          configured: data.configured
        });
        toast.success('Local server connected!');
      } else {
        setLocalServerStatus({ connected: false, error: 'Invalid response' });
        toast.error('Failed to connect to local server');
      }
    } catch (error) {
      setLocalServerStatus({ connected: false, error: error.message });
      toast.error('Cannot connect to local server');
    } finally {
      setCheckingLocalServer(false);
    }
  };

  // Save printer settings for a terminal
  const handleSavePrinterSettings = async () => {
    if (!printerTerminal) return;
    
    setSavingPrinter(true);
    try {
      await storeTerminalsAPI.update(printerTerminal.terminal_id, {
        printer_settings: printerSettings
      });
      
      // Update local state
      setTerminals(terminals.map(t => 
        t.terminal_id === printerTerminal.terminal_id 
          ? { ...t, printer_settings: printerSettings }
          : t
      ));
      
      toast.success(`Printer settings saved for ${printerTerminal.terminal_id}`);
      setShowPrinterModal(false);
      setPrinterTerminal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save printer settings');
    } finally {
      setSavingPrinter(false);
    }
  };

  // Save all hardware settings for a terminal
  const handleSaveHardwareSettings = async () => {
    if (!hardwareTerminal) return;
    
    setSavingHardware(true);
    try {
      await storeTerminalsAPI.update(hardwareTerminal.terminal_id, {
        hardware_settings: hardwareSettings
      });
      
      // Update local state
      setTerminals(terminals.map(t => 
        t.terminal_id === hardwareTerminal.terminal_id 
          ? { ...t, hardware_settings: hardwareSettings }
          : t
      ));
      
      toast.success(`Hardware settings saved for ${hardwareTerminal.terminal_id}`);
      setShowHardwareModal(false);
      setHardwareTerminal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save hardware settings');
    } finally {
      setSavingHardware(false);
    }
  };

  // Open hardware settings modal for a terminal
  const openHardwareSettings = (terminal) => {
    setHardwareTerminal(terminal);
    // Load existing settings or use defaults
    const existing = terminal.hardware_settings || {};
    setHardwareSettings({
      printer: {
        enabled: true,
        print_method: existing.printer?.print_method || 'browser',
        printer_name: existing.printer?.printer_name || terminal.printer_settings?.default_printer || '',
        paper_width: existing.printer?.paper_width || terminal.printer_settings?.paper_width || 80,
        auto_print: existing.printer?.auto_print ?? terminal.printer_settings?.auto_print ?? true,
        print_copies: existing.printer?.print_copies || terminal.printer_settings?.print_copies || 1
      },
      cash_drawer: {
        enabled: existing.cash_drawer?.enabled || false,
        connection_type: existing.cash_drawer?.connection_type || 'printer',
        auto_open_on: existing.cash_drawer?.auto_open_on || 'cash',
        kick_command: existing.cash_drawer?.kick_command || 'ESC p 0 25 250'
      },
      customer_display: {
        enabled: existing.customer_display?.enabled || false,
        display_type: existing.customer_display?.display_type || 'none',
        connection: existing.customer_display?.connection || 'com',
        port_or_ip: existing.customer_display?.port_or_ip || '',
        show_items: existing.customer_display?.show_items ?? true,
        show_total: existing.customer_display?.show_total ?? true,
        welcome_message: existing.customer_display?.welcome_message || 'Welcome to SY Store!'
      },
      payment_terminal: {
        enabled: existing.payment_terminal?.enabled || false,
        terminal_brand: existing.payment_terminal?.terminal_brand || 'manual',
        connection_type: existing.payment_terminal?.connection_type || 'usb',
        device_id: existing.payment_terminal?.device_id || '',
        ip_address: existing.payment_terminal?.ip_address || '',
        integration_mode: existing.payment_terminal?.integration_mode || 'manual'
      }
    });
    setShowHardwareModal(true);
  };

  const openLocalStorePOS = () => {
    navigate('/local-store');
  };

  // Save bill terms
  const handleSaveBillTerms = async () => {
    setSavingBillTerms(true);
    try {
      await api.put(`/stores/${storeId}/bill-terms`, { bill_terms: billTerms });
      setStoreInfo({ ...storeInfo, bill_terms: billTerms });
      toast.success('Bill terms saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bill terms');
    } finally {
      setSavingBillTerms(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="store-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Settings</h1>
          <p className="text-gray-500">Manage terminals and local server configuration</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="terminals" className="data-[state=active]:bg-white">
            <Monitor className="w-4 h-4 mr-2" />
            Terminals
          </TabsTrigger>
          <TabsTrigger value="hardware" className="data-[state=active]:bg-white">
            <Cpu className="w-4 h-4 mr-2" />
            Hardware
          </TabsTrigger>
          <TabsTrigger value="bill-terms" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" />
            Bill Terms
          </TabsTrigger>
          <TabsTrigger value="staff-assignment" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Staff Assignment
          </TabsTrigger>
          <TabsTrigger value="local-server" className="data-[state=active]:bg-white">
            <Server className="w-4 h-4 mr-2" />
            Local Server (Offline)
          </TabsTrigger>
        </TabsList>

        {/* Terminals Tab */}
        <TabsContent value="terminals" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="w-5 h-5" />
                    POS Terminals ({terminals.length}/5)
                  </CardTitle>
                  <CardDescription>
                    Each terminal can have its own independent cash register
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    setNewTerminalName('');
                    setNewTerminalType('pos');
                    setShowAddModal(true);
                  }}
                  disabled={terminals.length >= 5}
                  data-testid="add-terminal-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Terminal
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {terminals.length === 0 ? (
                <div className="text-center py-12">
                  <Monitor className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No terminals configured</p>
                  <p className="text-sm text-gray-400 mb-4">Create your first terminal to enable multi-register billing</p>
                  <Button onClick={() => setShowAddModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Terminal
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {terminals.map((terminal) => (
                    <div
                      key={terminal.terminal_id}
                      className="border rounded-lg p-4 border-gray-200 hover:border-emerald-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-600">
                            <Monitor className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold">{terminal.terminal_id}</p>
                            <p className="text-sm text-gray-500">{terminal.terminal_name}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-gray-500 capitalize">
                          Type: {terminal.terminal_type}
                        </span>
                        {terminal.has_open_register && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            Register Open
                          </Badge>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(terminal)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteTerminal(terminal.terminal_id)}
                          disabled={terminal.has_open_register}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>How terminals work:</strong> Each terminal operates independently with its own cash register.
              Use the terminal selector in the top bar to switch between terminals. Sales and cash registers are tracked per terminal.
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* Printers Tab */}
        <TabsContent value="hardware" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Cpu className="w-5 h-5" />
                    Terminal Hardware Configuration
                  </CardTitle>
                  <CardDescription>
                    Configure POS peripherals for each terminal: Printer, Cash Drawer, Customer Display, Payment Terminal
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {terminals.length === 0 ? (
                <div className="text-center py-12">
                  <Cpu className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No terminals configured</p>
                  <p className="text-sm text-gray-400">Create terminals first to configure their hardware</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Hardware Overview Table */}
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Terminal</TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Printer className="w-4 h-4" />
                            <span>Printer</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            <span>Cash Drawer</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Tv className="w-4 h-4" />
                            <span>Display</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <CreditCard className="w-4 h-4" />
                            <span>Payment</span>
                          </div>
                        </TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {terminals.map((terminal) => {
                        const hw = terminal.hardware_settings || {};
                        const printer = hw.printer || terminal.printer_settings;
                        return (
                          <TableRow key={terminal.terminal_id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Monitor className="w-4 h-4 text-gray-400" />
                                <div>
                                  <p className="font-medium">{terminal.terminal_id}</p>
                                  <p className="text-xs text-gray-500">{terminal.terminal_name}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {printer?.printer_name || printer?.default_printer ? (
                                <div className="flex flex-col items-center">
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                  <span className="text-[10px] text-gray-500 mt-0.5">
                                    {hw.printer?.print_method === 'qz_tray' ? 'QZ Tray' : 'Browser'}
                                  </span>
                                </div>
                              ) : (
                                <AlertCircle className="w-5 h-5 text-gray-300 mx-auto" />
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {hw.cash_drawer?.enabled ? (
                                <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                              ) : (
                                <AlertCircle className="w-5 h-5 text-gray-300 mx-auto" />
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {hw.customer_display?.enabled ? (
                                <div className="flex flex-col items-center">
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                  <span className="text-[10px] text-gray-500 mt-0.5">
                                    {hw.customer_display.display_type === 'vfd_pole' ? 'VFD' : 
                                     hw.customer_display.display_type === 'lcd_pole' ? 'LCD' :
                                     hw.customer_display.display_type === 'secondary_screen' ? 'Screen' : '-'}
                                  </span>
                                </div>
                              ) : (
                                <AlertCircle className="w-5 h-5 text-gray-300 mx-auto" />
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {hw.payment_terminal?.enabled ? (
                                <div className="flex flex-col items-center">
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                  <span className="text-[10px] text-gray-500 mt-0.5">
                                    {hw.payment_terminal.terminal_brand === 'pine_labs' ? 'Pine Labs' :
                                     hw.payment_terminal.terminal_brand === 'paytm' ? 'Paytm' :
                                     hw.payment_terminal.terminal_brand === 'razorpay' ? 'Razorpay' :
                                     hw.payment_terminal.terminal_brand === 'phonepe' ? 'PhonePe' :
                                     hw.payment_terminal.terminal_brand === 'mswipe' ? 'MSwipe' : 'Manual'}
                                  </span>
                                </div>
                              ) : (
                                <AlertCircle className="w-5 h-5 text-gray-300 mx-auto" />
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`configure-hardware-${terminal.terminal_id}`}
                                onClick={() => openHardwareSettings(terminal)}
                              >
                                <Settings className="w-4 h-4 mr-1" />
                                Configure
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Device Legend */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-sm mb-3">Supported Devices:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="font-medium flex items-center gap-1"><Printer className="w-4 h-4" /> Printers</p>
                        <p className="text-gray-500 text-xs">Epson, Star, Rongta, POS-80, Xprinter</p>
                      </div>
                      <div>
                        <p className="font-medium flex items-center gap-1"><DollarSign className="w-4 h-4" /> Cash Drawers</p>
                        <p className="text-gray-500 text-xs">RJ11 (via printer) or USB</p>
                      </div>
                      <div>
                        <p className="font-medium flex items-center gap-1"><Tv className="w-4 h-4" /> Displays</p>
                        <p className="text-gray-500 text-xs">VFD Pole, LCD Pole, Secondary Screen</p>
                      </div>
                      <div>
                        <p className="font-medium flex items-center gap-1"><CreditCard className="w-4 h-4" /> Payment</p>
                        <p className="text-gray-500 text-xs">Pine Labs, Paytm, Razorpay, PhonePe</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bill Terms Tab */}
        <TabsContent value="bill-terms" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Bill Terms & Conditions
                  </CardTitle>
                  <CardDescription>
                    Configure terms and conditions that will be printed on every receipt/bill
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bill-terms">Terms & Conditions</Label>
                <Textarea
                  id="bill-terms"
                  data-testid="bill-terms-textarea"
                  value={billTerms}
                  onChange={(e) => setBillTerms(e.target.value)}
                  placeholder={`Enter your store's terms and conditions here. Example:

TERMS & CONDITIONS

No exchange or refund once goods are sold.

Warranty (if applicable) is valid only for manufacturing defects.

Please check the product before leaving the store.

Goods once opened or used will not be taken back.

This bill must be produced for any warranty or service claim.`}
                  className="min-h-[300px] font-mono text-sm"
                />
                <p className="text-xs text-gray-500">
                  These terms will be printed at the bottom of every receipt after the QR code section.
                </p>
              </div>
              
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveBillTerms}
                  disabled={savingBillTerms}
                  data-testid="save-bill-terms-btn"
                >
                  {savingBillTerms ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Bill Terms
                </Button>
              </div>
              
              {/* Preview */}
              {billTerms && (
                <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-semibold text-sm mb-2">Preview on Receipt:</h4>
                  <div className="bg-white p-4 border rounded text-xs font-mono whitespace-pre-wrap max-w-[280px] mx-auto">
                    <div className="text-center font-bold mb-2">TERMS & CONDITIONS</div>
                    <div className="text-[10px] leading-tight">{billTerms}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Assignment Tab */}
        <TabsContent value="staff-assignment" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Staff Terminal Assignment
                  </CardTitle>
                  <CardDescription>
                    Assign terminals to cashiers. Cashiers can only access their assigned terminals.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {staffList.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No staff members found</p>
                  <p className="text-sm text-gray-400">Staff members will appear here after they are added to the store</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Staff Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Assigned Terminals</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffList.map((staff) => (
                      <TableRow key={staff.user_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{staff.full_name}</p>
                            <p className="text-xs text-gray-500">@{staff.username}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={staff.role_name?.toLowerCase().includes('manager') ? 'default' : 'outline'}>
                            {staff.role_name}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {staff.role_name?.toLowerCase().includes('manager') ? (
                            <span className="text-sm text-emerald-600 font-medium">All Terminals (Manager)</span>
                          ) : staff.assigned_terminals?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {staff.terminal_names?.map((name, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {staff.assigned_terminals[idx]} - {name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-amber-600">No terminal assigned</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {!staff.role_name?.toLowerCase().includes('manager') && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`assign-terminal-btn-${staff.user_id}`}
                              onClick={() => {
                                console.log('Opening assign modal for:', staff.full_name);
                                setSelectedStaff(staff);
                                // Initialize assignments for this staff
                                const assignments = {};
                                terminals.forEach(t => {
                                  assignments[t.terminal_id] = staff.assigned_terminals?.includes(t.terminal_id) || false;
                                });
                                setStaffAssignments(assignments);
                                setShowAssignModal(true);
                              }}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Assign
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> Cashiers can only see and use terminals assigned to them. 
              Managers automatically have access to all terminals. Unassigned cashiers cannot access POS or Cash Register.
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* Local Server Tab */}
        <TabsContent value="local-server" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                Local Store Server (Offline Mode)
              </CardTitle>
              <CardDescription>
                Configure a local server for offline POS operations when internet is unavailable
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Local Server URL Configuration */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Local Server URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={localServerUrl}
                      onChange={(e) => setLocalServerUrl(e.target.value)}
                      placeholder="http://localhost:8002"
                      className="flex-1"
                      data-testid="local-server-url-input"
                    />
                    <Button variant="outline" onClick={handleSaveLocalServerUrl}>
                      Save
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleCheckLocalServer}
                      disabled={checkingLocalServer}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${checkingLocalServer ? 'animate-spin' : ''}`} />
                      Test
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    The URL where your local store server is running (typically http://localhost:8002)
                  </p>
                </div>

                {/* Connection Status */}
                {localServerStatus && (
                  <div className={`p-4 rounded-lg border ${
                    localServerStatus.connected 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      {localServerStatus.connected ? (
                        <>
                          <Wifi className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-green-800">Connected to Local Server</p>
                            <p className="text-sm text-green-600">
                              {localServerStatus.storeName} ({localServerStatus.storeCode})
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <WifiOff className="w-5 h-5 text-red-600" />
                          <div>
                            <p className="font-medium text-red-800">Cannot Connect</p>
                            <p className="text-sm text-red-600">{localServerStatus.error}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="pt-4 border-t">
                <h3 className="font-medium mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto py-4 justify-start"
                    onClick={openLocalStorePOS}
                    data-testid="open-local-pos-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Monitor className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Open Local Store POS</p>
                        <p className="text-xs text-gray-500">Access offline mode POS billing</p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                  </Button>

                  <Button
                    variant="outline"
                    className="h-auto py-4 justify-start"
                    onClick={() => navigate('/local-store/settings')}
                    data-testid="local-settings-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Settings className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Local Server Settings</p>
                        <p className="text-xs text-gray-500">Manage terminals & sync</p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                  </Button>
                </div>
              </div>

              {/* Setup Instructions */}
              <div className="pt-4 border-t">
                <h3 className="font-medium mb-2">Setup Instructions</h3>
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600 space-y-2">
                  <p><strong>1.</strong> Install the local store server on the store machine</p>
                  <p><strong>2.</strong> Start the server (default port: 8002)</p>
                  <p><strong>3.</strong> Enter the server URL above and test the connection</p>
                  <p><strong>4.</strong> Initialize the server with HQ credentials (one-time setup)</p>
                  <p><strong>5.</strong> Use the Open Local Store POS button to access offline billing</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> The local server enables offline billing when internet is unavailable.
              Sales made offline are automatically synced to HQ when connection is restored.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>

      {/* Add Terminal Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent aria-describedby="add-terminal-description">
          <DialogHeader>
            <DialogTitle>Create New Terminal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p id="add-terminal-description" className="text-sm text-gray-500 sr-only">
              Create a new POS terminal for your store
            </p>
            <div>
              <Label>Terminal Name</Label>
              <Input
                value={newTerminalName}
                onChange={(e) => setNewTerminalName(e.target.value)}
                placeholder="e.g., Counter 1, Checkout A"
                data-testid="terminal-name-input"
              />
            </div>
            <div>
              <Label>Terminal Type</Label>
              <Select value={newTerminalType} onValueChange={setNewTerminalType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pos">POS Counter</SelectItem>
                  <SelectItem value="kiosk">Self-Service Kiosk</SelectItem>
                  <SelectItem value="mobile">Mobile Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                Terminal ID will be auto-generated as: 
                <span className="font-mono text-emerald-600 ml-1">
                  {storeInfo?.code || 'STORE'}-T{terminals.length + 1}
                </span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTerminal} disabled={processing}>
              {processing ? 'Creating...' : 'Create Terminal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Terminal Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent aria-describedby="edit-terminal-description">
          <DialogHeader>
            <DialogTitle>Edit Terminal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p id="edit-terminal-description" className="text-sm text-gray-500 sr-only">
              Edit terminal name and type
            </p>
            <div>
              <Label>Terminal ID</Label>
              <Input value={editingTerminal?.terminal_id || ''} disabled />
            </div>
            <div>
              <Label>Terminal Name</Label>
              <Input
                value={newTerminalName}
                onChange={(e) => setNewTerminalName(e.target.value)}
              />
            </div>
            <div>
              <Label>Terminal Type</Label>
              <Select value={newTerminalType} onValueChange={setNewTerminalType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pos">POS Counter</SelectItem>
                  <SelectItem value="kiosk">Self-Service Kiosk</SelectItem>
                  <SelectItem value="mobile">Mobile Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditTerminal} disabled={processing}>
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Assignment Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md" aria-describedby="assign-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Assign Terminals to {selectedStaff?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p id="assign-dialog-description" className="text-sm text-gray-500">
              Select which terminals this staff member can access:
            </p>
            <div className="space-y-3">
              {terminals.map((terminal) => (
                <div
                  key={terminal.terminal_id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={terminal.terminal_id}
                      checked={staffAssignments[terminal.terminal_id] || false}
                      onCheckedChange={(checked) => {
                        setStaffAssignments(prev => ({
                          ...prev,
                          [terminal.terminal_id]: checked
                        }));
                      }}
                    />
                    <div>
                      <label htmlFor={terminal.terminal_id} className="font-medium cursor-pointer">
                        {terminal.terminal_id}
                      </label>
                      <p className="text-xs text-gray-500">{terminal.terminal_name}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {terminal.terminal_type}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!selectedStaff) return;
                setProcessing(true);
                try {
                  // Get current and new assignments
                  const currentAssigned = selectedStaff.assigned_terminals || [];
                  const newAssignments = Object.entries(staffAssignments)
                    .filter(([_, checked]) => checked)
                    .map(([id]) => id);
                  
                  // Remove unassigned
                  for (const terminalId of currentAssigned) {
                    if (!newAssignments.includes(terminalId)) {
                      await storeTerminalsAPI.unassignUser(terminalId, selectedStaff.user_id);
                    }
                  }
                  
                  // Add new assignments
                  for (const terminalId of newAssignments) {
                    if (!currentAssigned.includes(terminalId)) {
                      await storeTerminalsAPI.assignUser(terminalId, selectedStaff.user_id);
                    }
                  }
                  
                  toast.success('Terminal assignments updated');
                  setShowAssignModal(false);
                  loadData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to update assignments');
                } finally {
                  setProcessing(false);
                }
              }}
              disabled={processing}
            >
              {processing ? 'Saving...' : 'Save Assignments'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Printer Settings Modal */}
      <Dialog open={showPrinterModal} onOpenChange={setShowPrinterModal}>
        <DialogContent className="max-w-md" aria-describedby="printer-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-600" />
              Configure Printer - {printerTerminal?.terminal_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p id="printer-dialog-description" className="text-sm text-gray-500">
              Set up the default thermal printer for bill printing on this terminal.
            </p>
            
            {/* Printer Name */}
            <div className="space-y-2">
              <Label htmlFor="printer-name">Default Printer Name *</Label>
              <Input
                id="printer-name"
                value={printerSettings.default_printer || ''}
                onChange={(e) => setPrinterSettings({
                  ...printerSettings,
                  default_printer: e.target.value
                })}
                placeholder="e.g., POS-80, EPSON TM-T20II, Star TSP100"
                data-testid="printer-name-input"
              />
              <p className="text-xs text-gray-500">
                Enter the exact printer name as shown in your system's printer list
              </p>
            </div>

            {/* Paper Width */}
            <div className="space-y-2">
              <Label>Paper Width</Label>
              <Select 
                value={String(printerSettings.paper_width || 80)} 
                onValueChange={(v) => setPrinterSettings({
                  ...printerSettings,
                  paper_width: parseInt(v)
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select paper width" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="58">58mm (2.25 inch) - Small</SelectItem>
                  <SelectItem value="80">80mm (3.15 inch) - Standard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Print Copies */}
            <div className="space-y-2">
              <Label htmlFor="print-copies">Print Copies</Label>
              <Select 
                value={String(printerSettings.print_copies || 1)} 
                onValueChange={(v) => setPrinterSettings({
                  ...printerSettings,
                  print_copies: parseInt(v)
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select copies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Copy (Customer only)</SelectItem>
                  <SelectItem value="2">2 Copies (Customer + Store)</SelectItem>
                  <SelectItem value="3">3 Copies</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Auto Print */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Auto Print After Sale</Label>
                <p className="text-xs text-gray-500">Automatically print bill after completing sale</p>
              </div>
              <Checkbox
                checked={printerSettings.auto_print !== false}
                onCheckedChange={(checked) => setPrinterSettings({
                  ...printerSettings,
                  auto_print: checked
                })}
              />
            </div>

            {/* Preview Info */}
            <Alert className="bg-amber-50 border-amber-200">
              <Info className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800">
                <strong>Note:</strong> For thermal printing to work directly, you may need to install 
                printer drivers and ensure the browser has printer access. Alternatively, use the 
                built-in print preview (Ctrl+P).
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrinterModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSavePrinterSettings} 
              disabled={savingPrinter || !printerSettings.default_printer}
            >
              {savingPrinter ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hardware Configuration Modal */}
      <Dialog open={showHardwareModal} onOpenChange={setShowHardwareModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="hardware-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5 text-blue-600" />
              Hardware Configuration - {hardwareTerminal?.terminal_id}
            </DialogTitle>
          </DialogHeader>
          <p id="hardware-dialog-description" className="text-sm text-gray-500">
            Configure all POS peripherals for this terminal.
          </p>
          
          <div className="space-y-6 py-4">
            {/* ========== PRINTER SECTION ========== */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Printer className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold">Bill Printer</h3>
                </div>
                <Checkbox
                  checked={hardwareSettings.printer.enabled}
                  onCheckedChange={(checked) => setHardwareSettings({
                    ...hardwareSettings,
                    printer: { ...hardwareSettings.printer, enabled: checked }
                  })}
                />
              </div>
              
              {hardwareSettings.printer.enabled && (
                <div className="space-y-4">
                  {/* Print Method */}
                  <div className="space-y-2">
                    <Label>Print Method *</Label>
                    <Select 
                      value={hardwareSettings.printer.print_method}
                      onValueChange={(v) => setHardwareSettings({
                        ...hardwareSettings,
                        printer: { ...hardwareSettings.printer, print_method: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="browser">Browser Print Dialog (Default)</SelectItem>
                        <SelectItem value="qz_tray">QZ Tray (Direct Silent Print)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      {hardwareSettings.printer.print_method === 'qz_tray' 
                        ? 'Requires QZ Tray installed on POS computer (qz.io)' 
                        : 'Opens system print dialog - select printer manually'}
                    </p>
                  </div>

                  {/* Printer Name */}
                  <div className="space-y-2">
                    <Label>Printer Name *</Label>
                    <Input
                      value={hardwareSettings.printer.printer_name}
                      onChange={(e) => setHardwareSettings({
                        ...hardwareSettings,
                        printer: { ...hardwareSettings.printer, printer_name: e.target.value }
                      })}
                      placeholder="e.g., POS-80, EPSON TM-T20II, Star TSP100"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Paper Width */}
                    <div className="space-y-2">
                      <Label>Paper Width</Label>
                      <Select 
                        value={String(hardwareSettings.printer.paper_width)}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          printer: { ...hardwareSettings.printer, paper_width: parseInt(v) }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="58">58mm</SelectItem>
                          <SelectItem value="80">80mm</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Print Copies */}
                    <div className="space-y-2">
                      <Label>Print Copies</Label>
                      <Select 
                        value={String(hardwareSettings.printer.print_copies)}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          printer: { ...hardwareSettings.printer, print_copies: parseInt(v) }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Copy</SelectItem>
                          <SelectItem value="2">2 Copies</SelectItem>
                          <SelectItem value="3">3 Copies</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Auto Print */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <Label className="font-medium">Auto Print After Sale</Label>
                      <p className="text-xs text-gray-500">Automatically print bill after completing sale</p>
                    </div>
                    <Checkbox
                      checked={hardwareSettings.printer.auto_print}
                      onCheckedChange={(checked) => setHardwareSettings({
                        ...hardwareSettings,
                        printer: { ...hardwareSettings.printer, auto_print: checked }
                      })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ========== CASH DRAWER SECTION ========== */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold">Cash Drawer</h3>
                </div>
                <Checkbox
                  checked={hardwareSettings.cash_drawer.enabled}
                  onCheckedChange={(checked) => setHardwareSettings({
                    ...hardwareSettings,
                    cash_drawer: { ...hardwareSettings.cash_drawer, enabled: checked }
                  })}
                />
              </div>
              
              {hardwareSettings.cash_drawer.enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Connection Type */}
                    <div className="space-y-2">
                      <Label>Connection Type</Label>
                      <Select 
                        value={hardwareSettings.cash_drawer.connection_type}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          cash_drawer: { ...hardwareSettings.cash_drawer, connection_type: v }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="printer">Via Printer (RJ11 Cable)</SelectItem>
                          <SelectItem value="usb">USB Direct</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Auto Open */}
                    <div className="space-y-2">
                      <Label>Auto Open On</Label>
                      <Select 
                        value={hardwareSettings.cash_drawer.auto_open_on}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          cash_drawer: { ...hardwareSettings.cash_drawer, auto_open_on: v }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash Sales Only</SelectItem>
                          <SelectItem value="all">All Sales</SelectItem>
                          <SelectItem value="manual">Manual Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Kick Command */}
                  <div className="space-y-2">
                    <Label>Drawer Kick Command (ESC/POS)</Label>
                    <Input
                      value={hardwareSettings.cash_drawer.kick_command}
                      onChange={(e) => setHardwareSettings({
                        ...hardwareSettings,
                        cash_drawer: { ...hardwareSettings.cash_drawer, kick_command: e.target.value }
                      })}
                      placeholder="ESC p 0 25 250"
                    />
                    <p className="text-xs text-gray-500">Default: ESC p 0 25 250 (works with most drawers)</p>
                  </div>
                </div>
              )}
            </div>

            {/* ========== CUSTOMER DISPLAY SECTION ========== */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Tv className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold">Customer Display</h3>
                </div>
                <Checkbox
                  checked={hardwareSettings.customer_display.enabled}
                  onCheckedChange={(checked) => setHardwareSettings({
                    ...hardwareSettings,
                    customer_display: { ...hardwareSettings.customer_display, enabled: checked }
                  })}
                />
              </div>
              
              {hardwareSettings.customer_display.enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Display Type */}
                    <div className="space-y-2">
                      <Label>Display Type</Label>
                      <Select 
                        value={hardwareSettings.customer_display.display_type}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          customer_display: { ...hardwareSettings.customer_display, display_type: v }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vfd_pole">VFD Pole Display (20x2)</SelectItem>
                          <SelectItem value="lcd_pole">LCD Pole Display</SelectItem>
                          <SelectItem value="secondary_screen">Secondary Screen/Tablet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Connection */}
                    <div className="space-y-2">
                      <Label>Connection</Label>
                      <Select 
                        value={hardwareSettings.customer_display.connection}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          customer_display: { ...hardwareSettings.customer_display, connection: v }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="com">COM Port (Serial)</SelectItem>
                          <SelectItem value="usb">USB</SelectItem>
                          <SelectItem value="network">Network IP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Port/IP */}
                  <div className="space-y-2">
                    <Label>
                      {hardwareSettings.customer_display.connection === 'network' ? 'IP Address' : 
                       hardwareSettings.customer_display.connection === 'com' ? 'COM Port' : 'Device Path'}
                    </Label>
                    <Input
                      value={hardwareSettings.customer_display.port_or_ip}
                      onChange={(e) => setHardwareSettings({
                        ...hardwareSettings,
                        customer_display: { ...hardwareSettings.customer_display, port_or_ip: e.target.value }
                      })}
                      placeholder={hardwareSettings.customer_display.connection === 'network' ? '192.168.1.100' : 
                                   hardwareSettings.customer_display.connection === 'com' ? 'COM3' : '/dev/usb0'}
                    />
                  </div>

                  {/* Welcome Message */}
                  <div className="space-y-2">
                    <Label>Welcome Message</Label>
                    <Input
                      value={hardwareSettings.customer_display.welcome_message}
                      onChange={(e) => setHardwareSettings({
                        ...hardwareSettings,
                        customer_display: { ...hardwareSettings.customer_display, welcome_message: e.target.value }
                      })}
                      placeholder="Welcome to SY Store!"
                    />
                  </div>

                  {/* Display Options */}
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={hardwareSettings.customer_display.show_items}
                        onCheckedChange={(checked) => setHardwareSettings({
                          ...hardwareSettings,
                          customer_display: { ...hardwareSettings.customer_display, show_items: checked }
                        })}
                      />
                      <Label className="text-sm">Show Items</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={hardwareSettings.customer_display.show_total}
                        onCheckedChange={(checked) => setHardwareSettings({
                          ...hardwareSettings,
                          customer_display: { ...hardwareSettings.customer_display, show_total: checked }
                        })}
                      />
                      <Label className="text-sm">Show Total</Label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ========== PAYMENT TERMINAL SECTION ========== */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold">Payment Terminal (Card/QR)</h3>
                </div>
                <Checkbox
                  checked={hardwareSettings.payment_terminal.enabled}
                  onCheckedChange={(checked) => setHardwareSettings({
                    ...hardwareSettings,
                    payment_terminal: { ...hardwareSettings.payment_terminal, enabled: checked }
                  })}
                />
              </div>
              
              {hardwareSettings.payment_terminal.enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Terminal Brand */}
                    <div className="space-y-2">
                      <Label>Terminal Brand</Label>
                      <Select 
                        value={hardwareSettings.payment_terminal.terminal_brand}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          payment_terminal: { ...hardwareSettings.payment_terminal, terminal_brand: v }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual Entry</SelectItem>
                          <SelectItem value="pine_labs">Pine Labs</SelectItem>
                          <SelectItem value="paytm">Paytm EDC</SelectItem>
                          <SelectItem value="razorpay">Razorpay POS</SelectItem>
                          <SelectItem value="phonepe">PhonePe Smart Speaker</SelectItem>
                          <SelectItem value="mswipe">MSwipe</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Connection Type */}
                    <div className="space-y-2">
                      <Label>Connection</Label>
                      <Select 
                        value={hardwareSettings.payment_terminal.connection_type}
                        onValueChange={(v) => setHardwareSettings({
                          ...hardwareSettings,
                          payment_terminal: { ...hardwareSettings.payment_terminal, connection_type: v }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usb">USB</SelectItem>
                          <SelectItem value="bluetooth">Bluetooth</SelectItem>
                          <SelectItem value="network">Network IP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Device ID */}
                    <div className="space-y-2">
                      <Label>Device/Terminal ID</Label>
                      <Input
                        value={hardwareSettings.payment_terminal.device_id}
                        onChange={(e) => setHardwareSettings({
                          ...hardwareSettings,
                          payment_terminal: { ...hardwareSettings.payment_terminal, device_id: e.target.value }
                        })}
                        placeholder="e.g., TID123456"
                      />
                    </div>

                    {/* IP Address (if network) */}
                    {hardwareSettings.payment_terminal.connection_type === 'network' && (
                      <div className="space-y-2">
                        <Label>IP Address</Label>
                        <Input
                          value={hardwareSettings.payment_terminal.ip_address}
                          onChange={(e) => setHardwareSettings({
                            ...hardwareSettings,
                            payment_terminal: { ...hardwareSettings.payment_terminal, ip_address: e.target.value }
                          })}
                          placeholder="192.168.1.200"
                        />
                      </div>
                    )}
                  </div>

                  {/* Integration Mode */}
                  <div className="space-y-2">
                    <Label>Integration Mode</Label>
                    <Select 
                      value={hardwareSettings.payment_terminal.integration_mode}
                      onValueChange={(v) => setHardwareSettings({
                        ...hardwareSettings,
                        payment_terminal: { ...hardwareSettings.payment_terminal, integration_mode: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual (Enter amount on device)</SelectItem>
                        <SelectItem value="integrated">Integrated (Auto-send amount)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      {hardwareSettings.payment_terminal.integration_mode === 'integrated' 
                        ? 'Payment amount will be automatically sent to the terminal' 
                        : 'Cashier manually enters amount on the payment device'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHardwareModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveHardwareSettings} 
              disabled={savingHardware}
            >
              {savingHardware ? 'Saving...' : 'Save All Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreSettingsPage;
